import React from "react";
import { FormattedMessage, type IntlShape, useIntl } from "react-intl";
import { Button, Card, CardHeader, CardActions } from "@mui/material";

import { productionGroupSchema } from "@/schemas";

const getStatusColor = (machineGroupsOnline: number | undefined): "success.main" | "text.disabled" => {
	if (machineGroupsOnline && machineGroupsOnline > 0) {
		return "success.main";
	}
	return "text.disabled";
};

export interface ProductionGroupCardProps {
	readonly productionGroup: unknown;
	onClick(): void;
	readonly machineGroupsOnline?: number;
}

const getMachineGroupsOnlineMessage = (machineGroupsOnline: number | undefined, intl: IntlShape): string => {
	if (machineGroupsOnline === undefined) {
		return "";
	}

	return machineGroupsOnline > 0
		? intl.formatMessage(
				{
					id: "{machineGroupsOnline} Machine Groups Online",
					defaultMessage: "{machineGroupsOnline} Machine Groups Online",
				},
				{ machineGroupsOnline },
			)
		: intl.formatMessage({ id: "Machine Groups Unavailable", defaultMessage: "Machine Groups Unavailable" });
};

export const ProductionGroupCard: React.FC<ProductionGroupCardProps> = ({
	productionGroup,
	onClick,
	machineGroupsOnline,
}) => {
	const intl = useIntl();
	const validationResult = productionGroupSchema
		.enrich({ callSite: "ProductionGroupCard" })
		.safeParseWithTelemetry(productionGroup);

	if (validationResult.success === false) {
		return null;
	}

	const { alias } = validationResult.data;

	return (
		<Card onClick={onClick} sx={{ cursor: "pointer" }} data-testid="production-group-card-container">
			<CardHeader
				title={alias}
				subheader={getMachineGroupsOnlineMessage(machineGroupsOnline, intl)}
				slotProps={{
					subheader: {
						sx: {
							color: getStatusColor(machineGroupsOnline),
						},
					},
				}}
			/>

			<CardActions>
				<Button
					variant="contained"
					fullWidth
					data-pendoid="select-production-group__button__join-production-group"
					data-testid="join-production-group-button"
				>
					{machineGroupsOnline !== undefined ? (
						<FormattedMessage id="Use Production Group" defaultMessage="Use Production Group" />
					) : (
						<FormattedMessage id="Join Production Group" defaultMessage="Join Production Group" />
					)}
				</Button>
			</CardActions>
		</Card>
	);
};
